"use client";
import React, { useState, useMemo, useEffect, useRef } from "react";
import EntryPoint from "./EntryPoint";
import EntryPointInline from "./EntryPointInline";
import MultipleChoice from "./Quiz/MultipleChoice";
import FindYourPathResults from "./FindYourPathResults";
import defaultQuizOptions from "./Quiz/quizOptions.json";
import { QuizConfig, QuizSection } from "./type";
import { useDynamicOptions } from "./hooks/useDynamicOptions";
import { useQuizSearch } from "./hooks/useQuizSearch";
import { useFirstViewVariant } from "./hooks/useFirstViewVariant";
import useSWR from "swr";
import { FACET_FETCH_LIMIT } from "@/components/Search/constants";
import { PRODUCT_SUMMARY_INDEX_EN } from "@/constants/algolia";
import { buildFacetFiltersFromQuiz } from "@/lib/productSummary/buildFacetFiltersFromQuiz";
import clientSearch from "@/lib/algolia/clientSearch";
import { useAuth } from "@/components/AuthProvider";
import {
  clearGuidedPathResults,
  loadPersistedGuidedPathResults,
  persistGuidedPathResults,
} from "./utils/persistedResults";
import getFilters from "../Search/utils/getFilters";
import {
  FindYourPathInitialView,
  FindYourPathLayoutVariant,
} from "@/monarch/rules/findYourPath/types";

type ViewState = "entry" | "quiz" | "results";

interface GuidedPathWidgetProps {
  customQuizSections?: QuizSection[];
  layoutVariant?: FindYourPathLayoutVariant;
  firstView?: FindYourPathInitialView;
}

const GuidedPathWidget = ({
  customQuizSections,
  layoutVariant = "default",
  firstView,
}: GuidedPathWidgetProps) => {
  const { isLoggedIn, isAuthReady } = useAuth();

  // Determine initial view with priority:
  // 1. Monarch config (firstView prop)
  // 2. Login state (logged in -> quiz, logged out -> entry)
  const getDefaultView = (): ViewState => {
    if (firstView) {
      return firstView;
    }
    return isLoggedIn ? "quiz" : "entry";
  };

  const [currentView, setCurrentView] = useState<ViewState>(getDefaultView());
  const [currentSectionIndex, setCurrentSectionIndex] = useState(0);
  const [answers, setAnswers] = useState<Record<string, string | string[]>>({});
  const [shouldFocusEntryHeading, setShouldFocusEntryHeading] = useState(false);
  const [shouldFocusResultsHeading, setShouldFocusResultsHeading] =
    useState(false);
  const entryHeadingRef = useRef<HTMLHeadingElement>(null);

  // Track layout variant for first view only - switches to default after interaction
  const {
    currentVariant: quizVariant,
    markInteracted,
    resetInteraction,
  } = useFirstViewVariant(layoutVariant);

  // Fetch user's language preference from account (only if logged in)
  const { data: userLanguageData } = useSWR(
    isLoggedIn && isAuthReady ? "/api/user-language" : null,
    async (url: string) => {
      const res = await fetch(url);
      return res.json() as Promise<{ language: string | null }>;
    },
    {
      fallbackData: { language: null },
      revalidateOnFocus: false,
      dedupingInterval: 60000, // Cache for 1 minute
    },
  );

  // Use user's account language preference (defaults to "en" if not set)
  const language = userLanguageData?.language || "en";

  // Use custom sections from Monarch if available, otherwise use default
  const quizConfig: QuizConfig = useMemo(() => {
    if (customQuizSections && customQuizSections.length > 0) {
      return { sections: customQuizSections };
    }
    return defaultQuizOptions as QuizConfig;
  }, [customQuizSections]);

  // Memoize searchable keys to prevent unnecessary re-renders
  const searchableKeys = useMemo(
    () =>
      new Set(
        quizConfig.sections
          .filter((section) => section.searchFilterKey)
          .map((section) => section.searchFilterKey!)
          .filter((key): key is string => key !== undefined),
      ),
    [quizConfig.sections],
  );

  // Quiz search hook
  const {
    results: searchResults,
    isLoading: isSearchLoading,
    executeSearch,
    hydrateResults,
    reset: resetSearch,
  } = useQuizSearch({
    searchableKeys,
    quizConfig,
    language,
  });
  const hasHydratedFromStorage = useRef(false);

  useEffect(() => {
    if (!isLoggedIn) {
      hasHydratedFromStorage.current = false;
    }
  }, [isLoggedIn]);

  useEffect(() => {
    if (
      !isAuthReady ||
      !isLoggedIn ||
      hasHydratedFromStorage.current ||
      typeof window === "undefined"
    ) {
      return;
    }

    const parsed = loadPersistedGuidedPathResults();
    if (!parsed) {
      hasHydratedFromStorage.current = true;
      return;
    }

    const restoredAnswers = Object.entries(parsed.answers ?? {}).reduce(
      (acc, [key, value]) => {
        acc[key] = Array.isArray(value) ? [...value] : value;
        return acc;
      },
      {} as Record<string, string | string[]>,
    );

    setAnswers(restoredAnswers);
    hydrateResults(parsed.results ? [...parsed.results] : []);
    setCurrentView("results");
    hasHydratedFromStorage.current = true;
  }, [hydrateResults, isAuthReady, isLoggedIn]);

  useEffect(() => {
    if (currentView === "entry" && shouldFocusEntryHeading) {
      const headingElement = entryHeadingRef.current;
      if (headingElement) {
        headingElement.focus();
        setShouldFocusEntryHeading(false);
      }
    }
  }, [currentView, shouldFocusEntryHeading]);

  const handleGetStarted = () => {
    setCurrentView("quiz");
    setCurrentSectionIndex(0);
    setShouldFocusEntryHeading(false);
    // When entry CTA is clicked, mark as interacted to switch to default variant
    markInteracted();
  };

  const handleAnswer = (section: QuizSection, answer: string | string[]) => {
    // Use searchFilterKey for searchable questions, or key for non-searchable
    const key = section.searchFilterKey || section.key || "unknown";
    setAnswers((prev: Record<string, string | string[]>) => ({
      ...prev,
      [key]: answer,
    }));
  };

  // Helper to check if next section should be auto-skipped
  const getAvailableOptionsForSection = async (
    sectionIndex: number,
    currentAnswers: Record<string, string | string[]>,
  ) => {
    const section = quizConfig.sections[sectionIndex];
    if (!section) return [];

    // If no searchFilterKey, use static options
    if (!section.searchFilterKey) {
      return section.options || [];
    }

    // Build facet filters for this check, excluding current section
    const facetFilters = buildFacetFiltersFromQuiz(
      currentAnswers,
      searchableKeys,
      quizConfig,
      section.searchFilterKey,
    );

    // Collect facets to fetch
    const facetsToFetch = new Set<string>([section.searchFilterKey]);
    if (section.options) {
      section.options.forEach((option) => {
        if (option.facetMappings) {
          Object.keys(option.facetMappings).forEach((facetKey) => {
            facetsToFetch.add(facetKey);
          });
        }
      });
    }

    // Fetch from Algolia
    const response = await clientSearch([
      {
        indexName: PRODUCT_SUMMARY_INDEX_EN,
        params: {
          facets: Array.from(facetsToFetch),
          facetFilters,
          filters: getFilters(),
          hitsPerPage: 0,
          maxValuesPerFacet: FACET_FETCH_LIMIT,
        },
      },
    ]);

    const allFacets = response.results[0].facets || {};
    const currentFacets = allFacets[section.searchFilterKey] || {};

    // Filter static options if they exist
    if (section.options) {
      return section.options.filter((option) => {
        if (option.facetMappings) {
          return Object.entries(option.facetMappings).every(
            ([facetKey, facetValue]) => {
              const facetData = allFacets[facetKey] || {};
              return facetValue in facetData;
            },
          );
        }
        const valueToCheck =
          option.searchFilterValue || option.value || option.label;
        return valueToCheck in currentFacets;
      });
    }

    return [];
  };

  const handleNext = async (finalAnswer?: string | string[]) => {
    // Save current answer
    const currentAnswers = { ...answers };
    if (finalAnswer !== undefined) {
      const key =
        currentSection.searchFilterKey || currentSection.key || "unknown";
      currentAnswers[key] = finalAnswer;
    }

    // Check if we're done with quiz
    if (currentSectionIndex >= quizConfig.sections.length - 1) {
      if (currentSectionIndex === 0) {
        markInteracted();
      }
      // Quiz completed, execute search with quiz answers
      await handleSearchExecution(currentAnswers);
      return;
    }

    // Move to next section
    let nextIndex = currentSectionIndex + 1;

    // Check if next section has only one option and auto-skip
    try {
      let nextOptions = await getAvailableOptionsForSection(
        nextIndex,
        currentAnswers,
      );

      // Auto-skip if only one option
      while (
        nextOptions.length === 1 &&
        nextIndex < quizConfig.sections.length
      ) {
        const nextSection = quizConfig.sections[nextIndex];
        const onlyOption = nextOptions[0];
        const optionValue =
          onlyOption.searchFilterValue || onlyOption.value || onlyOption.label;

        // Save the auto-selected answer
        const key = nextSection.searchFilterKey || nextSection.key || "unknown";
        currentAnswers[key] = optionValue;

        // Move to next section
        nextIndex++;

        // If we've reached the end, execute search
        if (nextIndex >= quizConfig.sections.length) {
          if (currentSectionIndex === 0) {
            markInteracted();
          }
          await handleSearchExecution(currentAnswers);
          return;
        }

        // Check next section and update nextOptions for the loop condition
        nextOptions = await getAvailableOptionsForSection(
          nextIndex,
          currentAnswers,
        );
      }
    } catch (error) {
      console.error("Error checking next section:", error);
    }

    // Update state with all accumulated answers and new index
    // Mark as interacted when advancing from the first question
    // (batched with setCurrentSectionIndex to avoid a flash of the default variant)
    if (currentSectionIndex === 0) {
      markInteracted();
    }
    setAnswers(currentAnswers);
    setCurrentSectionIndex(nextIndex);
  };

  const handleSearchExecution = async (
    completeAnswers: Record<string, string | string[]>,
  ) => {
    setCurrentView("results");
    setShouldFocusResultsHeading(true);
    const hits = await executeSearch(completeAnswers);
    persistGuidedPathResults({
      answers: completeAnswers,
      results: hits,
      timestamp: Date.now(),
    });
  };

  const handleBack = () => {
    if (currentSectionIndex > 0) {
      // Reset variant when navigating back to the first section
      // (only when firstView is "quiz", since the entry point already switches the variant)
      if (currentSectionIndex === 1 && firstView === "quiz") {
        resetInteraction();
      }
      setCurrentSectionIndex((prev: number) => prev - 1);
    }
  };

  const handleEditGoal = () => {
    const nextView = getDefaultView();
    setCurrentView(nextView);
    setCurrentSectionIndex(0);
    setAnswers({});
    clearGuidedPathResults();
    resetSearch();
    // Reset interaction state when starting over
    resetInteraction();
    if (nextView === "entry") {
      setShouldFocusEntryHeading(true);
    } else {
      setShouldFocusEntryHeading(false);
    }
    setShouldFocusResultsHeading(false);
  };

  const currentSection = quizConfig.sections[currentSectionIndex];

  // Always fetch dynamic options when searchFilterKey exists
  // This helps filter static options and determine what has results
  const shouldFetchDynamicOptions = !!currentSection?.searchFilterKey;

  // Get dynamic options - will filter static options if they exist
  const { options: dynamicOptions, isLoading: isLoadingOptions } =
    useDynamicOptions({
      filterCategory: shouldFetchDynamicOptions
        ? currentSection?.searchFilterKey
        : undefined,
      currentAnswers: answers,
      searchableKeys,
      staticOptions: currentSection?.options, // Pass static options to be filtered
      quizConfig,
    });

  const renderEntryPoint = () => {
    switch (layoutVariant) {
      case "inline":
        return (
          <EntryPointInline
            onGetStarted={handleGetStarted}
            headingRef={entryHeadingRef}
          />
        );
      case "default":
      default:
        return (
          <EntryPoint
            onGetStarted={handleGetStarted}
            headingRef={entryHeadingRef}
          />
        );
    }
  };

  const renderCurrentView = () => {
    switch (currentView) {
      case "entry":
        return renderEntryPoint();

      case "quiz":
        return (
          <MultipleChoice
            section={currentSection}
            dynamicOptions={
              shouldFetchDynamicOptions ? dynamicOptions : undefined
            }
            currentAnswer={
              answers[
                currentSection.searchFilterKey || currentSection.key || ""
              ]
            }
            onAnswer={(answer) => handleAnswer(currentSection, answer)}
            onBack={handleBack}
            onNext={(answer) => handleNext(answer)}
            totalSections={quizConfig.sections.length}
            currentSectionIndex={currentSectionIndex}
            allAnswers={answers}
            quizConfig={quizConfig}
            isLoadingOptions={shouldFetchDynamicOptions && isLoadingOptions}
            variant={quizVariant}
          />
        );

      case "results":
        return (
          <FindYourPathResults
            searchResults={searchResults}
            quizAnswers={answers}
            onEditGoal={handleEditGoal}
            isLoading={isSearchLoading}
            shouldFocusHeading={shouldFocusResultsHeading}
            onHeadingFocused={() => setShouldFocusResultsHeading(false)}
          />
        );

      default:
        return renderEntryPoint();
    }
  };

  return (
    <section
      className="w-full"
      role="region"
      aria-labelledby="guided-path-experience-heading"
    >
      <h2 id="guided-path-experience-heading" className="sr-only">
        Find your learning path experience
      </h2>
      {renderCurrentView()}
    </section>
  );
};

export default GuidedPathWidget;
